import { useEffect, useState, useCallback } from 'react';
import { lms, qms, dps, standards, auditApi } from '../lib/api';
import { toast } from 'sonner';
import {
  Truck, Award, ShieldAlert, FileBadge, Scroll, RefreshCw, Loader2,
  Search, AlertTriangle, CheckCircle2, Clock, Package,
} from 'lucide-react';

const SUB_TABS = [
  { id: 'lms', label: 'LMS · Logistics', Icon: Truck, color: '#0D47A1' },
  { id: 'qms', label: 'QMS · Quality', Icon: Award, color: '#1B5E20' },
  { id: 'dps', label: 'DPS · Privacy', Icon: ShieldAlert, color: '#AD1457' },
  { id: 'standards', label: 'Standards', Icon: FileBadge, color: '#5D4037' },
  { id: 'audit', label: 'Audit logs', Icon: Scroll, color: '#9A4500' },
];

export default function AdminAdvanced() {
  const [sub, setSub] = useState('lms');
  return (
    <div className="mt-6">
      <div className="flex gap-2 flex-wrap mb-5">
        {SUB_TABS.map(({ id, label, Icon, color }) => {
          const active = sub === id;
          return (
            <button
              key={id}
              data-testid={`adv-tab-${id}`}
              onClick={() => setSub(id)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border transition ${
                active
                  ? 'bg-ink text-white border-ink'
                  : 'bg-white text-ink/70 border-border hover:border-ink/40'
              }`}
              style={active ? { background: color, borderColor: color } : {}}
            >
              <Icon className="w-4 h-4"/>{label}
            </button>
          );
        })}
      </div>
      {sub === 'lms' && <LmsPane/>}
      {sub === 'qms' && <QmsPane/>}
      {sub === 'dps' && <DpsPane/>}
      {sub === 'standards' && <StandardsPane/>}
      {sub === 'audit' && <AuditPane/>}
    </div>
  );
}

// =============================================================
// LMS
// =============================================================
function LmsPane() {
  const [status, setStatus] = useState('');
  const [village, setVillage] = useState('');
  const [items, setItems] = useState([]);
  const [agents, setAgents] = useState([]);
  const [sla, setSla] = useState(null);
  const [cod, setCod] = useState(null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (village) params.village = village;
      const [s, a, sl, c] = await Promise.all([
        lms.shipments(params),
        lms.agents({}),
        lms.sla().catch(() => null),
        lms.codSummary().catch(() => null),
      ]);
      setItems(s.items || []);
      setAgents(a.items || a || []);
      setSla(sl);
      setCod(c);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load LMS data');
    } finally { setLoading(false); }
  }, [status, village]);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Shipments shown" value={items.length} Icon={Package}/>
        <StatCard label="Active agents" value={agents.filter(a => a.status === 'active').length} Icon={Truck}/>
        <StatCard label="SLA breached" value={sla ? ((sla.historical_breach_count || 0) + (sla.pending_pickup_breach?.length || 0) + (sla.pending_deliver_breach?.length || 0)) : '—'} Icon={AlertTriangle} tone="red"/>
        <StatCard label="COD pending" value={cod ? `₹${Math.round((cod.agents || []).reduce((s, a) => s + (a.uncollected || 0), 0))}` : '—'} Icon={Clock}/>
      </div>

      <div className="card-soft p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-bold text-ink/60 mb-1">Status</label>
            <select data-testid="lms-status-filter" value={status} onChange={e => setStatus(e.target.value)} className="border border-border rounded px-2 py-1.5 text-sm">
              <option value="">All</option>
              {['pending_assignment','assigned','picked_up','in_transit','out_for_delivery','delivered','failed','returned'].map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-ink/60 mb-1">Village</label>
            <input data-testid="lms-village-filter" value={village} onChange={e => setVillage(e.target.value)} placeholder="e.g. Rampur" className="border border-border rounded px-2 py-1.5 text-sm"/>
          </div>
          <button data-testid="lms-refresh-btn" onClick={reload} className="btn-secondary !py-2 inline-flex items-center gap-1.5">
            {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>} Refresh
          </button>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="lms-shipments-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-ink/50 border-b border-border">
                <th className="py-2 pr-3">Tracking</th>
                <th className="py-2 pr-3">Order</th>
                <th className="py-2 pr-3">Store</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Agent</th>
                <th className="py-2 pr-3">Village</th>
                <th className="py-2 pr-3">COD</th>
                <th className="py-2 pr-3">SLA</th>
              </tr>
            </thead>
            <tbody>
              {items.map(s => (
                <tr key={s.id} className="border-b border-border/60">
                  <td className="py-2 pr-3 font-mono text-xs">{s.tracking_id || s.id?.slice(0,8)}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{s.order_code || s.order_id?.slice(0,8)}</td>
                  <td className="py-2 pr-3">{s.store_name || '—'}</td>
                  <td className="py-2 pr-3"><Badge>{s.status}</Badge></td>
                  <td className="py-2 pr-3">{s.agent_name || <span className="text-ink/40">unassigned</span>}</td>
                  <td className="py-2 pr-3">{s.village || '—'}</td>
                  <td className="py-2 pr-3">{s.cod_amount ? `₹${Math.round(s.cod_amount)}` : '—'} <span className="text-xs text-ink/50">{s.cod_status || ''}</span></td>
                  <td className="py-2 pr-3">{(s.sla?.pickup_breached || s.sla?.deliver_breached) ? <span className="text-[#C62828] font-semibold">breached</span> : <span className="text-[#1B5E20]">ok</span>}</td>
                </tr>
              ))}
              {!items.length && !loading && (
                <tr><td colSpan={8} className="py-6 text-center text-ink/50">No shipments match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// =============================================================
// QMS
// =============================================================
function QmsPane() {
  const [entity, setEntity] = useState('store');
  const [tier, setTier] = useState('');
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recomputing, setRecomputing] = useState(false);

  const fetcher = {
    store: qms.stores,
    product: qms.products,
    employee: qms.employees,
    delivery_agent: qms.agents,
  }[entity];

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tier) params.tier = tier;
      if (q) params.q = q;
      const res = await fetcher(params);
      setItems(res.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load QMS scores');
    } finally { setLoading(false); }
  }, [fetcher, tier, q]);
  useEffect(() => { reload(); }, [reload]);

  const onRecompute = async () => {
    setRecomputing(true);
    try {
      const r = await qms.recomputeAll();
      toast.success(`Recomputed ${r.total || 'all'} scorecards`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Recompute failed');
    } finally { setRecomputing(false); }
  };

  const tierColor = (t) => ({
    excellent: 'bg-[#E8F5E9] text-[#1B5E20]',
    good: 'bg-[#E3F2FD] text-[#0D47A1]',
    warning: 'bg-[#FFF8E1] text-[#9A7B0A]',
    risk: 'bg-[#FBE9E7] text-[#BF360C]',
    critical: 'bg-[#FFEBEE] text-[#C62828]',
  }[t] || 'bg-ink/10 text-ink/70');

  return (
    <div className="space-y-5">
      <div className="card-soft p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-bold text-ink/60 mb-1">Entity</label>
            <select data-testid="qms-entity-filter" value={entity} onChange={e => setEntity(e.target.value)} className="border border-border rounded px-2 py-1.5 text-sm">
              <option value="store">Stores</option>
              <option value="product">Products</option>
              <option value="employee">Employees</option>
              <option value="delivery_agent">Delivery agents</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-ink/60 mb-1">Tier</label>
            <select data-testid="qms-tier-filter" value={tier} onChange={e => setTier(e.target.value)} className="border border-border rounded px-2 py-1.5 text-sm">
              <option value="">All</option>
              {['excellent','good','warning','risk','critical'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-ink/60 mb-1">Search</label>
            <input data-testid="qms-search" value={q} onChange={e => setQ(e.target.value)} placeholder="name…" className="border border-border rounded px-2 py-1.5 text-sm"/>
          </div>
          <button data-testid="qms-refresh-btn" onClick={reload} className="btn-secondary !py-2 inline-flex items-center gap-1.5">
            {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>} Refresh
          </button>
          <button data-testid="qms-recompute-btn" onClick={onRecompute} disabled={recomputing} className="btn-primary !py-2 inline-flex items-center gap-1.5 disabled:opacity-60">
            {recomputing ? <Loader2 className="w-4 h-4 animate-spin"/> : <Award className="w-4 h-4"/>}
            Recompute all
          </button>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="qms-scores-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-ink/50 border-b border-border">
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">Score</th>
                <th className="py-2 pr-3">Tier</th>
                <th className="py-2 pr-3">Last computed</th>
              </tr>
            </thead>
            <tbody>
              {items.map(s => (
                <tr key={`${s.entity_type}-${s.entity_id}`} className="border-b border-border/60">
                  <td className="py-2 pr-3">{s.entity_name || s.entity_id?.slice(0,8)}</td>
                  <td className="py-2 pr-3 font-semibold">{Math.round(s.score)} <span className="text-xs text-ink/40">/ 100</span></td>
                  <td className="py-2 pr-3"><span className={`text-xs font-bold px-2 py-0.5 rounded-full ${tierColor(s.tier_effective || s.tier)}`}>{s.tier_effective || s.tier}</span></td>
                  <td className="py-2 pr-3 text-xs text-ink/50">{s.last_computed_at ? new Date(s.last_computed_at).toLocaleString() : '—'}</td>
                </tr>
              ))}
              {!items.length && !loading && (
                <tr><td colSpan={4} className="py-6 text-center text-ink/50">No scores yet. Click <strong>Recompute all</strong>.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// =============================================================
// DPS
// =============================================================
function DpsPane() {
  const [tab, setTab] = useState('dsar');
  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap text-sm">
        {[
          { id: 'dsar', label: 'DSAR requests' },
          { id: 'griev', label: 'Grievances' },
          { id: 'breach', label: 'Breach log' },
          { id: 'sos', label: 'SOS / Safety' },
        ].map(t => (
          <button key={t.id} data-testid={`dps-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 rounded-md font-semibold border transition ${tab === t.id ? 'bg-[#AD1457] text-white border-[#AD1457]' : 'bg-white border-border text-ink/70'}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'dsar' && <DsarTable/>}
      {tab === 'griev' && <GrievanceTable/>}
      {tab === 'breach' && <BreachTable/>}
      {tab === 'sos' && <SosTable/>}
    </div>
  );
}

function DsarTable() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await dps.dsar(status ? { status } : {});
      setItems(r.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  }, [status]);
  useEffect(() => { reload(); }, [reload]);

  const updateStatus = async (id, newStatus) => {
    try {
      await dps.updateDsar(id, { status: newStatus });
      toast.success(`DSAR ${newStatus}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  return (
    <div className="card-soft p-4">
      <div className="flex gap-3 items-end mb-3">
        <select value={status} onChange={e => setStatus(e.target.value)} className="border border-border rounded px-2 py-1.5 text-sm" data-testid="dsar-status-filter">
          <option value="">All</option>
          {['received','verified','in_progress','fulfilled','rejected'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={reload} className="btn-secondary !py-2 inline-flex items-center gap-1.5" data-testid="dsar-refresh-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>} Refresh
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="dsar-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-ink/50 border-b border-border">
              <th className="py-2 pr-3">Phone</th>
              <th className="py-2 pr-3">Type</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Created</th>
              <th className="py-2 pr-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(d => (
              <tr key={d.id} className="border-b border-border/60">
                <td className="py-2 pr-3 font-mono">{d.user_phone || '—'}</td>
                <td className="py-2 pr-3">{d.request_type}</td>
                <td className="py-2 pr-3"><Badge>{d.status}</Badge></td>
                <td className="py-2 pr-3 text-xs text-ink/50">{d.created_at ? new Date(d.created_at).toLocaleDateString() : '—'}</td>
                <td className="py-2 pr-3 space-x-1">
                  {['verified','in_progress','fulfilled','rejected'].filter(s => s !== d.status).map(s => (
                    <button key={s} onClick={() => updateStatus(d.id, s)} data-testid={`dsar-${d.id}-${s}`} className="text-xs px-2 py-1 rounded border border-border hover:border-ink/40">{s}</button>
                  ))}
                </td>
              </tr>
            ))}
            {!items.length && !loading && <tr><td colSpan={5} className="py-6 text-center text-ink/50">No DSAR requests.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GrievanceTable() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('');
  const [overdue, setOverdue] = useState(false);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await dps.grievances({ status: status || undefined, overdue });
      setItems(r.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  }, [status, overdue]);
  useEffect(() => { reload(); }, [reload]);

  const updateStatus = async (id, newStatus) => {
    try {
      await dps.updateGrievance(id, { status: newStatus });
      toast.success(`Grievance ${newStatus}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  return (
    <div className="card-soft p-4">
      <div className="flex gap-3 items-end mb-3">
        <select value={status} onChange={e => setStatus(e.target.value)} className="border border-border rounded px-2 py-1.5 text-sm" data-testid="griev-status-filter">
          <option value="">All</option>
          {['open','in_progress','resolved','rejected'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className="text-sm flex items-center gap-1.5">
          <input type="checkbox" checked={overdue} onChange={e => setOverdue(e.target.checked)} data-testid="griev-overdue-toggle"/>
          Overdue only
        </label>
        <button onClick={reload} className="btn-secondary !py-2 inline-flex items-center gap-1.5" data-testid="griev-refresh-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>} Refresh
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="griev-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-ink/50 border-b border-border">
              <th className="py-2 pr-3">Subject</th>
              <th className="py-2 pr-3">Phone</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">SLA due</th>
              <th className="py-2 pr-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(g => (
              <tr key={g.id} className="border-b border-border/60">
                <td className="py-2 pr-3">{g.subject || '—'}</td>
                <td className="py-2 pr-3 font-mono">{g.user_phone || '—'}</td>
                <td className="py-2 pr-3"><Badge>{g.status}</Badge></td>
                <td className="py-2 pr-3 text-xs text-ink/50">{g.sla_due_at ? new Date(g.sla_due_at).toLocaleString() : '—'}</td>
                <td className="py-2 pr-3 space-x-1">
                  {['in_progress','resolved','rejected'].filter(s => s !== g.status).map(s => (
                    <button key={s} onClick={() => updateStatus(g.id, s)} data-testid={`griev-${g.id}-${s}`} className="text-xs px-2 py-1 rounded border border-border hover:border-ink/40">{s}</button>
                  ))}
                </td>
              </tr>
            ))}
            {!items.length && !loading && <tr><td colSpan={5} className="py-6 text-center text-ink/50">No grievances.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BreachTable() {
  const [items, setItems] = useState([]);
  const [openOnly, setOpenOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await dps.breach({ open_only: openOnly });
      setItems(r.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  }, [openOnly]);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="card-soft p-4">
      <div className="flex gap-3 items-center mb-3">
        <label className="text-sm flex items-center gap-1.5">
          <input type="checkbox" checked={openOnly} onChange={e => setOpenOnly(e.target.checked)} data-testid="breach-open-toggle"/>
          Open only
        </label>
        <button onClick={reload} className="btn-secondary !py-2 inline-flex items-center gap-1.5" data-testid="breach-refresh-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>} Refresh
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="breach-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-ink/50 border-b border-border">
              <th className="py-2 pr-3">Code</th>
              <th className="py-2 pr-3">Severity</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Discovered</th>
              <th className="py-2 pr-3">DPDP due</th>
            </tr>
          </thead>
          <tbody>
            {items.map(b => (
              <tr key={b.id} className="border-b border-border/60">
                <td className="py-2 pr-3 font-mono">{b.breach_code}</td>
                <td className="py-2 pr-3"><Badge>{b.severity}</Badge></td>
                <td className="py-2 pr-3"><Badge>{b.status}</Badge></td>
                <td className="py-2 pr-3 text-xs text-ink/50">{b.discovered_at ? new Date(b.discovered_at).toLocaleString() : '—'}</td>
                <td className="py-2 pr-3 text-xs text-ink/50">{b.dpdp_notification_due_by ? new Date(b.dpdp_notification_due_by).toLocaleString() : '—'}</td>
              </tr>
            ))}
            {!items.length && !loading && <tr><td colSpan={5} className="py-6 text-center text-ink/50">No breaches logged.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SosTable() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await dps.sosList();
      setItems(r.items || r || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);
  return (
    <div className="card-soft p-4">
      <div className="flex gap-3 items-center mb-3">
        <button onClick={reload} className="btn-secondary !py-2 inline-flex items-center gap-1.5" data-testid="sos-refresh-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>} Refresh
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="sos-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-ink/50 border-b border-border">
              <th className="py-2 pr-3">Phone</th>
              <th className="py-2 pr-3">Reason</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">At</th>
            </tr>
          </thead>
          <tbody>
            {items.map(s => (
              <tr key={s.id} className="border-b border-border/60">
                <td className="py-2 pr-3 font-mono">{s.user_phone || '—'}</td>
                <td className="py-2 pr-3">{s.reason || '—'}</td>
                <td className="py-2 pr-3"><Badge>{s.status}</Badge></td>
                <td className="py-2 pr-3 text-xs text-ink/50">{s.created_at ? new Date(s.created_at).toLocaleString() : '—'}</td>
              </tr>
            ))}
            {!items.length && !loading && <tr><td colSpan={4} className="py-6 text-center text-ink/50">No SOS events.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================
// Standards Registry
// =============================================================
function StandardsPane() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [bodyFilter, setBodyFilter] = useState('');
  const [mandatory, setMandatory] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (bodyFilter) params.body = bodyFilter;
      if (mandatory !== '') params.mandatory = mandatory === 'true';
      const r = await standards.list(params);
      setItems(r.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  }, [q, bodyFilter, mandatory]);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="space-y-4">
      <div className="card-soft p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-bold text-ink/60 mb-1">Search</label>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="code or title…" className="border border-border rounded px-2 py-1.5 text-sm" data-testid="std-search"/>
          </div>
          <div>
            <label className="block text-xs font-bold text-ink/60 mb-1">Body</label>
            <select value={bodyFilter} onChange={e => setBodyFilter(e.target.value)} className="border border-border rounded px-2 py-1.5 text-sm" data-testid="std-body-filter">
              <option value="">All</option>
              {['BIS','FSSAI','ISO','IEC','BEE'].map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-ink/60 mb-1">Mandatory</label>
            <select value={mandatory} onChange={e => setMandatory(e.target.value)} className="border border-border rounded px-2 py-1.5 text-sm" data-testid="std-mandatory-filter">
              <option value="">All</option>
              <option value="true">Mandatory</option>
              <option value="false">Voluntary</option>
            </select>
          </div>
          <button onClick={reload} className="btn-secondary !py-2 inline-flex items-center gap-1.5" data-testid="std-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Search className="w-4 h-4"/>} Search
          </button>
          <button onClick={() => setShowAdd(v => !v)} className="btn-primary !py-2 inline-flex items-center gap-1.5" data-testid="std-add-toggle">
            {showAdd ? 'Cancel' : '+ Add standard'}
          </button>
        </div>

        {showAdd && <AddStandardForm onCreated={() => { setShowAdd(false); reload(); }}/>}

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="std-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-ink/50 border-b border-border">
                <th className="py-2 pr-3">Code</th>
                <th className="py-2 pr-3">Title</th>
                <th className="py-2 pr-3">Body</th>
                <th className="py-2 pr-3">Categories</th>
                <th className="py-2 pr-3">Mandatory</th>
              </tr>
            </thead>
            <tbody>
              {items.map(s => (
                <tr key={s.code} className="border-b border-border/60">
                  <td className="py-2 pr-3 font-mono text-xs">{s.code}</td>
                  <td className="py-2 pr-3">{s.title}</td>
                  <td className="py-2 pr-3"><Badge>{s.body}</Badge></td>
                  <td className="py-2 pr-3 text-xs text-ink/60">{(s.categories || []).join(', ')}</td>
                  <td className="py-2 pr-3">{s.mandatory ? <CheckCircle2 className="w-4 h-4 text-[#1B5E20]"/> : <span className="text-ink/40">—</span>}</td>
                </tr>
              ))}
              {!items.length && !loading && <tr><td colSpan={5} className="py-6 text-center text-ink/50">No standards.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AddStandardForm({ onCreated }) {
  const [form, setForm] = useState({ code: '', title: '', body: 'BIS', categories: '', mandatory: false, body_text: '' });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await standards.add({
        code: form.code.trim(),
        title: form.title.trim(),
        body: form.body,
        categories: form.categories.split(',').map(s => s.trim()).filter(Boolean),
        mandatory: form.mandatory,
        body_text: form.body_text || undefined,
      });
      toast.success('Standard added');
      onCreated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add'); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="mt-4 p-4 bg-[#FAFAFA] rounded-lg border border-border grid sm:grid-cols-2 gap-3" data-testid="std-add-form">
      <input required value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} placeholder="Code (e.g. IS 1234:2010)" className="border border-border rounded px-2 py-1.5 text-sm" data-testid="std-code-input"/>
      <input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Title" className="border border-border rounded px-2 py-1.5 text-sm" data-testid="std-title-input"/>
      <select value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} className="border border-border rounded px-2 py-1.5 text-sm" data-testid="std-body-input">
        {['BIS','FSSAI','ISO','IEC','BEE'].map(b => <option key={b} value={b}>{b}</option>)}
      </select>
      <input value={form.categories} onChange={e => setForm({ ...form, categories: e.target.value })} placeholder="Categories (comma sep)" className="border border-border rounded px-2 py-1.5 text-sm" data-testid="std-cat-input"/>
      <label className="text-sm flex items-center gap-1.5 sm:col-span-2">
        <input type="checkbox" checked={form.mandatory} onChange={e => setForm({ ...form, mandatory: e.target.checked })} data-testid="std-mandatory-input"/>
        Mandatory
      </label>
      <textarea value={form.body_text} onChange={e => setForm({ ...form, body_text: e.target.value })} placeholder="Body text / description" rows={3} className="border border-border rounded px-2 py-1.5 text-sm sm:col-span-2" data-testid="std-bodytext-input"/>
      <button type="submit" disabled={saving} className="btn-primary !py-2 sm:col-span-2 disabled:opacity-60" data-testid="std-save-btn">
        {saving ? 'Saving…' : 'Save standard'}
      </button>
    </form>
  );
}

// =============================================================
// Audit logs
// =============================================================
function AuditPane() {
  const [tab, setTab] = useState('business');
  return (
    <div className="space-y-4">
      <div className="flex gap-2 text-sm">
        <button onClick={() => setTab('business')} data-testid="audit-tab-business" className={`px-3 py-1.5 rounded-md font-semibold border ${tab === 'business' ? 'bg-[#9A4500] text-white border-[#9A4500]' : 'bg-white border-border text-ink/70'}`}>Business audits</button>
        <button onClick={() => setTab('software')} data-testid="audit-tab-software" className={`px-3 py-1.5 rounded-md font-semibold border ${tab === 'software' ? 'bg-[#9A4500] text-white border-[#9A4500]' : 'bg-white border-border text-ink/70'}`}>Software trail</button>
      </div>
      {tab === 'business' ? <BusinessAuditTable/> : <SoftwareAuditTable/>}
    </div>
  );
}

function BusinessAuditTable() {
  const [items, setItems] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [r, h] = await Promise.all([
        auditApi.business({}),
        auditApi.health(90).catch(() => null),
      ]);
      setItems(r.items || []);
      setHealth(h);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-4 gap-3">
        <StatCard label="Total (90d)" value={health?.total_audits ?? '—'} Icon={Scroll}/>
        <StatCard label="Closed" value={health?.closed ?? '—'} Icon={CheckCircle2} tone="green"/>
        <StatCard label="Failed" value={health?.failed ?? '—'} Icon={AlertTriangle} tone="red"/>
        <StatCard label="Escalated" value={health?.escalated ?? '—'} Icon={AlertTriangle} tone="amber"/>
      </div>
      <div className="card-soft p-4">
        <div className="flex gap-3 items-center mb-3">
          <button onClick={reload} className="btn-secondary !py-2 inline-flex items-center gap-1.5" data-testid="audit-biz-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>} Refresh
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-biz-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-ink/50 border-b border-border">
                <th className="py-2 pr-3">Code</th>
                <th className="py-2 pr-3">Scope</th>
                <th className="py-2 pr-3">Subject</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Verdict</th>
                <th className="py-2 pr-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {items.map(a => (
                <tr key={a.id} className="border-b border-border/60">
                  <td className="py-2 pr-3 font-mono text-xs">{a.audit_code || a.id?.slice(0,8)}</td>
                  <td className="py-2 pr-3">{a.scope}</td>
                  <td className="py-2 pr-3 text-xs">{a.subject_kind}:{a.subject_id?.slice(0,8)}</td>
                  <td className="py-2 pr-3"><Badge>{a.status}</Badge></td>
                  <td className="py-2 pr-3">{a.verdict ? <Badge>{a.verdict}</Badge> : <span className="text-ink/40">—</span>}</td>
                  <td className="py-2 pr-3 text-xs text-ink/50">{a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
              {!items.length && !loading && <tr><td colSpan={6} className="py-6 text-center text-ink/50">No audits.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SoftwareAuditTable() {
  const [items, setItems] = useState([]);
  const [pathPrefix, setPathPrefix] = useState('');
  const [method, setMethod] = useState('');
  const [actorRole, setActorRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (pathPrefix) params.path_prefix = pathPrefix;
      if (method) params.method = method;
      if (actorRole) params.actor_role = actorRole;
      const [r, s] = await Promise.all([
        auditApi.software(params),
        auditApi.softwareSummary(7).catch(() => null),
      ]);
      setItems(r.items || []);
      setSummary(s);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  }, [pathPrefix, method, actorRole]);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-4 gap-3">
        <StatCard label="Events (7d)" value={summary?.total_writes ?? '—'} Icon={Scroll}/>
        <StatCard label="5xx errors" value={summary?.errors_5xx ?? '—'} Icon={AlertTriangle} tone="red"/>
        <StatCard label="Forbidden" value={summary?.forbidden_403 ?? '—'} Icon={AlertTriangle} tone="amber"/>
        <StatCard label="Unauthorized" value={summary?.unauthorized_401 ?? '—'} Icon={AlertTriangle} tone="amber"/>
      </div>
      <div className="card-soft p-4">
        <div className="flex gap-3 items-end mb-3 flex-wrap">
          <div>
            <label className="block text-xs font-bold text-ink/60 mb-1">Path prefix</label>
            <input value={pathPrefix} onChange={e => setPathPrefix(e.target.value)} placeholder="/api/orders" className="border border-border rounded px-2 py-1.5 text-sm" data-testid="audit-sw-path"/>
          </div>
          <div>
            <label className="block text-xs font-bold text-ink/60 mb-1">Method</label>
            <select value={method} onChange={e => setMethod(e.target.value)} className="border border-border rounded px-2 py-1.5 text-sm" data-testid="audit-sw-method">
              <option value="">All</option>
              {['POST','PUT','PATCH','DELETE'].map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-ink/60 mb-1">Actor role</label>
            <select value={actorRole} onChange={e => setActorRole(e.target.value)} className="border border-border rounded px-2 py-1.5 text-sm" data-testid="audit-sw-role">
              <option value="">All</option>
              {['admin','owner','customer','employee'].map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <button onClick={reload} className="btn-secondary !py-2 inline-flex items-center gap-1.5" data-testid="audit-sw-refresh">
            {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Search className="w-4 h-4"/>} Search
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-sw-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-ink/50 border-b border-border">
                <th className="py-2 pr-3">When</th>
                <th className="py-2 pr-3">Actor</th>
                <th className="py-2 pr-3">Method</th>
                <th className="py-2 pr-3">Path</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">IP</th>
              </tr>
            </thead>
            <tbody>
              {items.map(e => (
                <tr key={e.id || `${e.at}-${e.path}`} className="border-b border-border/60">
                  <td className="py-2 pr-3 text-xs text-ink/60">{e.at ? new Date(e.at).toLocaleString() : '—'}</td>
                  <td className="py-2 pr-3 text-xs">{e.actor_role || '—'} <span className="text-ink/40">{e.actor_phone || ''}</span></td>
                  <td className="py-2 pr-3 font-mono text-xs">{e.method}</td>
                  <td className="py-2 pr-3 font-mono text-xs truncate max-w-[280px]">{e.path}</td>
                  <td className="py-2 pr-3"><span className={`text-xs font-bold ${e.status_code >= 500 ? 'text-[#C62828]' : e.status_code >= 400 ? 'text-[#BF360C]' : 'text-[#1B5E20]'}`}>{e.status_code}</span></td>
                  <td className="py-2 pr-3 font-mono text-xs">{e.ip || '—'}</td>
                </tr>
              ))}
              {!items.length && !loading && <tr><td colSpan={6} className="py-6 text-center text-ink/50">No events.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// =============================================================
// Shared helpers
// =============================================================
function StatCard({ label, value, Icon, tone }) {
  const toneCls = {
    red: 'bg-[#FBE9E7] text-[#BF360C]',
    green: 'bg-[#E8F5E9] text-[#1B5E20]',
    amber: 'bg-[#FFF8E1] text-[#9A7B0A]',
  }[tone] || 'bg-[#E3F2FD] text-[#0D47A1]';
  return (
    <div className="card-soft p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg grid place-items-center ${toneCls}`}><Icon className="w-5 h-5"/></div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wider text-ink/50 font-semibold">{label}</div>
        <div className="font-heading font-bold text-xl truncate">{value}</div>
      </div>
    </div>
  );
}

function Badge({ children }) {
  return (
    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-ink/10 text-ink/80 inline-block">
      {children}
    </span>
  );
}
